import { Controller } from "@hotwired/stimulus"

// Tracks multiple typing users and displays an aggregated indicator.
// Typing events arrive as Turbo Stream appends to this element.
// Each append contains a hidden div with data-sender-id and data-sender-name.
export default class extends Controller {
  static values = {
    timeout: { type: Number, default: 3000 }
  }

  static targets = ["display"]

  connect() {
    this.typers = new Map()
    this.currentUserId = document.querySelector('meta[name="current-user-id"]')?.content

    // Watch for new typing event elements appended by Turbo Stream
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue
          if (node.dataset.senderId) {
            this.handleTypingEvent(node)
          }
        }
      }
    })
    this.observer.observe(this.element, { childList: true })
  }

  handleTypingEvent(el) {
    const senderId = el.dataset.senderId
    const name = el.dataset.senderName

    // Remove the event element immediately
    el.remove()

    if (!senderId || !name) return

    // Don't show own typing
    if (this.currentUserId && this.currentUserId === String(senderId)) return

    // Clear existing timeout for this typer
    const existing = this.typers.get(senderId)
    if (existing?.timeout) clearTimeout(existing.timeout)

    // Set new timeout to remove this typer
    const timeout = setTimeout(() => {
      this.typers.delete(senderId)
      this.render()
    }, this.timeoutValue)

    this.typers.set(senderId, { name, timeout })
    this.render()
  }

  render() {
    // Ensure display target exists (may have been cleared by stop_typing broadcast)
    let display = this.hasDisplayTarget ? this.displayTarget : null
    if (!display) {
      display = document.createElement("div")
      display.classList.add("typing-display")
      display.setAttribute("data-typing-tracker-target", "display")
      this.element.appendChild(display)
    }

    const names = Array.from(this.typers.values()).map(t => t.name)

    if (names.length === 0) {
      display.innerHTML = ""
      return
    }

    let text
    if (names.length === 1) {
      text = `${names[0]} is typing`
    } else if (names.length === 2) {
      text = `${names[0]} and ${names[1]} are typing`
    } else {
      text = `${names.length} people are typing`
    }

    display.innerHTML = `
      <div class="typing-indicator flex items-center gap-2 py-2">
        <div class="chat-typing-surface flex items-center gap-1.5 px-3 py-2 rounded-2xl rounded-bl-md">
          <div class="dot w-2 h-2 rounded-full"></div>
          <div class="dot w-2 h-2 rounded-full"></div>
          <div class="dot w-2 h-2 rounded-full"></div>
        </div>
        <span class="chat-text-tertiary text-xs italic ml-1">${text}</span>
      </div>
    `
  }

  disconnect() {
    this.observer?.disconnect()
    this.typers.forEach(({ timeout }) => clearTimeout(timeout))
    this.typers.clear()
  }
}
